import { useMemo } from 'react';
import { css } from '@emotion/react';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DatePickerField, FormRow, PhoneField, TextField, UseFormReturnType, Text } from '@frontend/design-system';

type CustomContactFormConfig = {
  firstName: {
    type: 'text';
    value: string;
    required: true;
  };
  lastName: {
    type: 'text';
    value: string;
    required: true;
  };
  dob: {
    type: 'datePicker';
    value: string;
    required: true;
    maxDate: string;
  };
  phone: {
    type: 'phone';
    value: string;
    required: true;
  };
};

type Props = {
  form: UseFormReturnType<CustomContactFormConfig, keyof CustomContactFormConfig>;
  locationId: string;
};

export const CustomContactForm = ({ form, locationId }: Props) => {
  const { t } = useTranslation('schedule');
  const { getErrors, getFieldProps, values } = form;
  const formErrors = useMemo(() => getErrors(), [getErrors]);

  // Search by phone numbers when entered phone number length is 10
  const duplicateContacts = PersonsV3.PersonQueries.useListPreferredPersonsByPhoneQuery(
    {
      limit: 1,
      locationId,
      phoneNumber: form.values.phone || '',
    },
    {
      enabled: form.values.phone?.length === 10,
      retry: 1,
    }
  );

  const hasDuplicateContact = useMemo(() => {
    return (duplicateContacts?.data?.persons || []).length > 0;
  }, [duplicateContacts]);

  return (
    <div css={containerStyles}>
      <FormRow css={formRowStyles}>
        <TextField
          {...getFieldProps('firstName')}
          label={t('First Name')}
          error={values.firstName === '' ? t('First name is required') : formErrors.firstName}
          autoFocus
        />
      </FormRow>
      <FormRow css={formRowStyles}>
        <TextField
          {...getFieldProps('lastName')}
          label={t('Last Name')}
          error={values.lastName === '' ? t('Last name is required') : formErrors.lastName}
        />
      </FormRow>
      <FormRow css={formRowStyles}>
        <DatePickerField
          {...getFieldProps('dob')}
          css={{ maxWidth: '100%' }}
          label={t('Date of Birth')}
          error={values.dob === '' ? t('Date of birth is required') : formErrors.dob}
        />
      </FormRow>
      <FormRow css={[formRowStyles, phoneRowStyles]}>
        <PhoneField
          {...getFieldProps('phone')}
          label={t('Phone')}
          error={values.phone === '' ? t('Phone number is required') : formErrors.phone}
        />
        {hasDuplicateContact && (
          <Text size='small' color='warn' css={warningStyles}>
            {t('A contact with this phone number already exists')}
          </Text>
        )}
      </FormRow>
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
});

const formRowStyles = css({
  margin: 0,
});

const phoneRowStyles = css({
  flexDirection: 'column',
  '& > div': {
    margin: 0,
    width: '100%',
  },
});

const warningStyles = css({
  margin: `${theme.spacing(1, 0, 0, 0)} !important`,
  color: theme.colors.warning60,
});
